import classNames from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import InputGroup from 'react-bootstrap/InputGroup'
import Nav from 'react-bootstrap/Nav'
import BSNavbar from 'react-bootstrap/Navbar'
import NavDropdown from 'react-bootstrap/NavDropdown'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import ReactGA from 'react-ga4'
import useClickAway from 'react-use/lib/useClickAway'
import { Persistor } from 'redux-persist/es/types'
import { PersistGate } from 'redux-persist/integration/react'
import { v4 } from 'uuid'

import crosswordIcon from '@/assets/crossword.svg'
import empty from '@/assets/empty.png'
import logo from '@/assets/logo.png'
import logoSmall from '@/assets/logoSmall.png'
import salt_48 from '@/assets/salt_48.png'
import spellifyIcon from '@/assets/spellify_icon.svg'
import AsyncTypeahead from '@/components/AsyncTypeahead/AsyncTypeahead'
import Clipboard from '@/components/Clipboard/Clipboard'
import Icon, { EDHRECIconsType } from '@/components/Icon/Icon'
import AnimatedHamburger from '@/components/Layout/AnimatedHamburger/AnimatedHamburger'
import MobileNav from '@/components/Layout/MobileNav/MobileNav'
import Mana, { ManaType } from '@/components/Mana/Mana'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import SettingsModal from '@/components/SettingsModal/SettingsModal'
import { COMBO_VOTE_IS_FINISHED } from '@/pages/combo-vote'
import { TOGGLE } from '@/store/clipboard/actions/types'
import { selectClipboardSize } from '@/store/clipboard/selectors'
import { useActionless, useAppSelector } from '@/store/hooks'
import { fetchMisc } from '@/store/misc/actions/fetchMisc'
import { SetType, TagType } from '@/store/misc/initialState'
import { AsyncTypeaheadEntryType } from '@/types/typeahead'

import styles from './Navbar.module.scss'

export const SALT_NAV_ENABLED = false
export const COMBO_NAV_ENABLED = !COMBO_VOTE_IS_FINISHED

type SubmenuType = {
  alt?: string
  className?: string
  color?: ManaType
  colors?: string
  href?: string
  icon?: EDHRECIconsType
  icon_svg_uri?: string
  id?: string
  invertedSvgFile?: string
  onClick?: () => void
  submenu?: SubmenuType[]
  title?: string
  value?: string | React.ReactNode
}

export type NavbarConfig = {
  background?: string
  href?: string
  icon?: EDHRECIconsType
  id: string
  submenu?: SubmenuType[]
  title: string
}

type Props = {
  persistor: Persistor
}

const Navbar = ({ persistor }: Props) => {
  const router = useRouter()
  const ref = useRef(null)
  const theme = 'light' // Pretend the theme is always light

  const clipboardIsOpen = useAppSelector((state) => state.clipboard.isOpen)
  const { articles, guides, precons, sets, tags } = useAppSelector((state) => state.misc)
  const displayName = useAppSelector((state) => state.user.displayName)
  const clipboardSize = useAppSelector(selectClipboardSize)
  const [toggleClipboard] = useActionless(TOGGLE)

  const [expanded, setExpanded] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  useClickAway(ref, (event: any) => {
    const clickedClipboardButton = event.target.closest('.edhrec-clipboard-dont-close')
    if (!clickedClipboardButton && clipboardIsOpen) {
      toggleClipboard()
    }
  })

  useEffect(() => {
    const handleRouteChange = () => setExpanded(false)

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => router.events.off('routeChangeComplete', handleRouteChange)
  }, [router.asPath])

  useEffect(() => {
    fetchMisc()
  }, [])

  const trackNavInternal = useCallback((destination: string, label?: string) => {
    if (process.env.NODE_ENV === 'production') {
      ReactGA.event({
        action: 'navbar_click',
        category: destination,
        label,
      })
    }
  }, [])

  const navbarConfig: NavbarConfig[] = [
    {
      background: 'https://card-images.edhrec.com/art_crop/front/d/c/dc69f960-68ba-4315-8146-6a7a82047503.jpg',
      icon: 'rss',
      id: 'navbar-articles',
      submenu: [
        { className: 'fw-semibold', href: '/articles', value: 'Articles' },
        ...articles.slice(0, 3).map((article: any) => ({
          alt: article.title,
          className: styles.indent,
          href: `/articles/${article.slug}`,
          value: (
            <div className='d-flex flex-column'>
              <span>{article.title}</span>{' '}
              <div className={styles.articleAuthor}>
                by <span className='text-orange'>{article.author}</span>
              </div>
            </div>
          ),
        })),
        { className: styles.indent, href: '/articles', value: 'More...' },
        { className: 'fw-semibold', href: '/guides', value: 'Guides' },
        ...guides.slice(0, 3).map((guide: any) => ({
          alt: guide.title,
          className: styles.indent,
          href: `/guides/${guide.slug}`,
          value: (
            <div className='d-flex flex-column'>
              <span>{guide.title}</span>{' '}
              <div className={styles.articleAuthor}>
                by <span className='text-orange'>{guide.author}</span>
              </div>
            </div>
          ),
        })),
        { className: styles.indent, href: '/guides', value: 'More...' },
      ],
      title: 'Articles',
    },
    {
      background: 'https://card-images.edhrec.com/art_crop/front/2/1/219210d3-263c-41c4-9211-0e59b2d4ccf3.jpg',
      icon: 'rankingStar',
      id: 'navbar-cards',
      submenu: [
        { href: '/top', value: 'Top Cards' },
        {
          id: 'navbar-cards-by-color',
          submenu: [
            { color: 'w', href: '/top/white', value: 'White' },
            { color: 'u', href: '/top/blue', value: 'Blue' },
            { color: 'b', href: '/top/black', value: 'Black' },
            { color: 'r', href: '/top/red', value: 'Red' },
            { color: 'g', href: '/top/green', value: 'Green' },
            { color: 'c', href: '/top/colorless', value: 'Colorless' },
            { color: 'multi', href: '/top/multicolor', value: 'Multicolor' },
          ],
          title: 'By Color',
        },
        {
          id: 'navbar-cards-by-type',
          submenu: [
            { href: '/top/creatures', value: 'Creatures' },
            { href: '/top/instants', value: 'Instants' },
            { href: '/top/sorceries', value: 'Sorceries' },
            { href: '/top/artifacts', value: 'Artifacts' },
            {
              className: styles.indent,
              href: '/top/equipment',
              value: 'Equipment',
            },
            {
              className: styles.indent,
              href: '/top/utility-artifacts',
              value: 'Utility Artifacts',
            },
            {
              className: styles.indent,
              href: '/top/mana-artifacts',
              value: 'Mana Artifacts',
            },
            { href: '/top/enchantments', value: 'Enchantments' },
            { className: styles.indent, href: '/top/auras', value: 'Auras' },
            { href: '/top/battles', value: 'Battles' },
            { href: '/top/planeswalkers', value: 'Planeswalkers' },
            { href: '/top/lands', value: 'Lands' },
            {
              className: styles.indent,
              href: '/top/utility-lands',
              value: 'Utility Lands',
            },
            {
              className: styles.indent,
              href: '/top/color-fixing-lands',
              value: 'Color-Fixing Lands',
            },
          ],
          title: 'By Type',
        },
        { href: '/combos', value: 'Combos' },
        { href: '/lands', value: 'Landbases' },
        { href: '/top/game-changers', value: 'Game Changers' },
        { href: '/top/salt', value: 'Saltiest Cards' },
        { href: '/banned', value: 'Banned Cards' },
      ],
      title: 'Cards',
    },
    {
      background: 'https://card-images.edhrec.com/art_crop/front/d/0/d0d33d52-3d28-4635-b985-51e126289259.jpg',
      icon: 'crown',
      id: 'navbar-commanders',
      submenu: [
        { href: '/commanders', value: 'Top Commanders' },
        { href: '/partners', value: 'Partners' },
        {
          id: 'navbar-commanders-mono',
          submenu: [
            { color: 'w', href: '/commanders/mono-white', value: 'White' },
            { color: 'u', href: '/commanders/mono-blue', value: 'Blue' },
            { color: 'b', href: '/commanders/mono-black', value: 'Black' },
            { color: 'r', href: '/commanders/mono-red', value: 'Red' },
            { color: 'g', href: '/commanders/mono-green', value: 'Green' },
            { color: 'c', href: '/commanders/colorless', value: 'Colorless' },
          ],
          title: 'Mono',
        },
        {
          id: 'navbar-commanders-2-color',
          submenu: [
            { colors: 'wu', href: '/commanders/azorius', value: 'Azorius' },
            { colors: 'ub', href: '/commanders/dimir', value: 'Dimir' },
            { colors: 'br', href: '/commanders/rakdos', value: 'Rakdos' },
            { colors: 'rg', href: '/commanders/gruul', value: 'Gruul' },
            { colors: 'gw', href: '/commanders/selesnya', value: 'Selesnya' },
            { colors: 'wb', href: '/commanders/orzhov', value: 'Orzhov' },
            { colors: 'ur', href: '/commanders/izzet', value: 'Izzet' },
            { colors: 'bg', href: '/commanders/golgari', value: 'Golgari' },
            { colors: 'rw', href: '/commanders/boros', value: 'Boros' },
            { colors: 'gu', href: '/commanders/simic', value: 'Simic' },
          ],
          title: '2 Color',
        },
        {
          id: 'navbar-commanders-3-color',
          submenu: [
            { colors: 'wub', href: '/commanders/esper', value: 'Esper' },
            { colors: 'ubr', href: '/commanders/grixis', value: 'Grixis' },
            { colors: 'brg', href: '/commanders/jund', value: 'Jund' },
            { colors: 'rgw', href: '/commanders/naya', value: 'Naya' },
            { colors: 'gwu', href: '/commanders/bant', value: 'Bant' },
            { colors: 'wbg', href: '/commanders/abzan', value: 'Abzan' },
            { colors: 'urw', href: '/commanders/jeskai', value: 'Jeskai' },
            { colors: 'bgu', href: '/commanders/sultai', value: 'Sultai' },
            { colors: 'rwb', href: '/commanders/mardu', value: 'Mardu' },
            { colors: 'gur', href: '/commanders/temur', value: 'Temur' },
          ],
          title: '3 Color',
        },
        {
          id: 'navbar-commanders-4-5-color',
          submenu: [
            {
              colors: 'wubr',
              href: '/commanders/yore-tiller',
              value: 'Yore-Tiller',
            },
            {
              colors: 'ubrg',
              href: '/commanders/glint-eye',
              value: 'Glint-Eye',
            },
            {
              colors: 'brgw',
              href: '/commanders/dune-brood',
              value: 'Dune-Brood',
            },
            {
              colors: 'rgwu',
              href: '/commanders/ink-treader',
              value: 'Ink-Treader',
            },
            {
              colors: 'gwub',
              href: '/commanders/witch-maw',
              value: 'Witch-Maw',
            },
            {
              colors: 'wubrg',
              href: '/commanders/five-color',
              value: '5 Color',
            },
          ],
          title: '4+ Color',
        },
      ],
      title: 'Commanders',
    },
    {
      background: 'https://card-images.edhrec.com/art_crop/front/4/b/4b71d89b-7ba4-406f-8736-ac62b9864f21.jpg',
      icon: 'cube',
      id: 'navbar-precons',
      submenu: [
        { href: '/precon', value: 'Precons' },
        ...precons.flatMap(
          ({ name, precons, tag }: { name: string; precons: { name: string; slug: string }[]; tag: string }) => [
            {
              alt: name,
              className: styles.indent,
              href: `/precon#${tag}`,
              value: <span className='fw-semibold'>{name}</span>,
            },
            ...precons.map((precon: { colors?: string; name: string; slug: string }) => ({
              className: styles.indentDouble,
              colors: precon.colors || '',
              href: `/precon/${precon.slug}`,
              value: precon.name,
            })),
          ],
        ),
        {
          className: styles.indent,
          href: '/precon',
          value: 'More...',
        },
      ],
      title: 'Precons',
    },
    {
      background: 'https://card-images.edhrec.com/art_crop/front/7/c/7c2839fa-33eb-47fb-9541-77c68612aa02.jpg',
      icon: 'layerGroup',
      id: 'navbar-sets',
      submenu: [
        { href: '/sets', value: 'Sets' },
        ...sets.map(({ code, icon_svg_uri, name }: SetType) => ({
          className: styles.indent,
          code,
          href: `/sets/${code}`,
          icon_svg_uri: icon_svg_uri || empty.src,
          value: name,
        })),
        {
          className: styles.indentDouble,
          href: '/sets',
          value: 'More...',
        },
      ],
      title: 'Sets',
    },
    {
      background: 'https://card-images.edhrec.com/art_crop/front/0/1/01033dae-fec1-41f2-b7f2-cc6a43331790.jpg',
      icon: 'tag',
      id: 'navbar-tags',
      submenu: [
        { href: '/tags', value: 'Tags' },
        ...tags.slice(0, 4).map(({ name, slug }: TagType) => ({
          className: styles.indent,
          href: `/tags/${slug}`,
          value: name,
        })),
        { className: styles.indent, href: '/tags', value: 'More...' },
        { href: '/tags/themes', value: 'Themes' },
        { href: '/tags/typal', value: 'Typal' },
        { href: '/companions', value: 'Companions' },
      ],
      title: 'Tags',
    },
  ]

  const SubNavItems = () => (
    <>
      <Nav.Link
        active={false}
        as={Link as any}
        className={classNames(styles.navbar2link, 'd-none d-lg-block')}
        href='/faq'
        onClick={() => trackNavInternal('/faq')}
        url='https://edhrec.com/faq'
      >
        <Icon className='me-1' fixedWidth icon='question' />
        FAQ
      </Nav.Link>
      <NavDropdown
        className={styles.dropdown}
        id='navbar-games'
        menuVariant='dark'
        title={
          <>
            <Icon className='me-1' fixedWidth icon='puzzlePiece' />
            Games
            {/*<span className={styles.newSuperscript}> NEW</span>*/}
          </>
        }
      >
        <NavDropdown.Item active={false} as={OutboundLink} url='https://edhrec.com/games/crossword' utm_medium='navbar'>
          <span className={styles.svg}>
            <img alt='Crossword' src={crosswordIcon.src} />
          </span>{' '}
          Crossword
        </NavDropdown.Item>
        <NavDropdown.Item active={false} as={OutboundLink} url='https://edhrec.com/games/spellify' utm_medium='navbar'>
          <span className={styles.svg}>
            <img alt='Spellify' src={spellifyIcon.src} />
          </span>{' '}
          Spellify
        </NavDropdown.Item>
        <NavDropdown.Item active={false} as={OutboundLink} url='https://edhrec.com/games/quiz' utm_medium='navbar'>
          <span className={styles.svg}>
            <Icon icon='squarePollVertical' />
          </span>
          &nbsp;Personality Quizzes
          {/*<span className={styles.newSuperscript}> NEW</span>*/}
        </NavDropdown.Item>
      </NavDropdown>
      <Nav.Link
        active={false}
        as={OutboundLink}
        className={styles.navbar2link}
        url='https://shop.spacecowmedia.com/collections/edhrec'
        utm_medium='navbar'
      >
        <Icon fixedWidth icon='shop' /> Merch
      </Nav.Link>
      <Nav.Link
        active={false}
        as={Link as any}
        className={styles.navbar2link}
        href='/newsletter'
        onClick={() => trackNavInternal('/newsletter')}
        url='https://edhrec.com/newsletter'
      >
        <Icon className='me-1' fixedWidth icon='newspaper' />
        Newsletter
      </Nav.Link>
      <Nav.Link
        active={false}
        as={OutboundLink}
        className={styles.navbar2link}
        url='https://www.patreon.com/edhrec'
        utm_medium='navbar'
      >
        <Icon className='me-1' fixedWidth icon='patreon' />
        Patreon
      </Nav.Link>
      <Nav.Link active={false} className={styles.navbar2link} href='/recs' onClick={() => trackNavInternal('/recs')}>
        <Icon className='me-1' fixedWidth icon='chartSimple' />
        Recs
      </Nav.Link>
      {SALT_NAV_ENABLED && (
        <Nav.Link
          active={false}
          as={Link as any}
          className={classNames(styles.navbar2link, styles.salt)}
          href='/salt'
          onClick={() => trackNavInternal('/salt')}
          url='https://edhrec.com/salt'
        >
          <img alt='Salt Score' className={styles.salt} src={salt_48.src} />
          Salt Vote
        </Nav.Link>
      )}
      {COMBO_NAV_ENABLED && (
        <Nav.Link
          active={false}
          as={Link as any}
          className={classNames(styles.navbar2link, styles.salt)}
          href='/combo-vote'
          onClick={() => trackNavInternal('/combo-vote')}
          url='https://edhrec.com/combo-vote'
        >
          <Icon className='me-1' fixedWidth icon='infinity' />
          Combo Vote
        </Nav.Link>
      )}
    </>
  )

  const handleRandom = async () => {
    const random = await fetch('https://edhrec.com/random/')
    const randomPath = random.url.replace('https://edhrec.com', '')
    trackNavInternal(randomPath, 'random')
    router.push(randomPath)
  }

  const handleSearch = async (value: AsyncTypeaheadEntryType[]) => {
    if (value.length > 0) {
      trackNavInternal(value[0].url, 'search')
      router.push(value[0].url)
    }
  }

  return (
    <>
      <SettingsModal handleClose={() => setShowSettings(false)} show={showSettings} />
      <div className={styles.spacer} />
      <BSNavbar bg='dark' className={classNames(styles.navbar, 'shadow-sm')} fixed='top' ref={ref} variant='dark'>
        <PersistGate persistor={persistor}>
          <Clipboard />
        </PersistGate>
        <BSNavbar.Brand className='d-none d-lg-flex'>
          <Link href='/' onClick={() => trackNavInternal('/')}>
            <Image alt='EDHREC' className={styles.logo} height={40} src={logo.src} width={148} />
          </Link>
        </BSNavbar.Brand>
        <div className={styles.brandSmall}>
          <Link href='/' onClick={() => trackNavInternal('/')}>
            <Image alt='EDHREC' className={styles.logo} height={40} src={logoSmall.src} width={40} />
          </Link>
          <InputGroup className={styles.searchSmall}>
            <AsyncTypeahead className={styles.typeahead} clearOnChange onChange={handleSearch} placeholder='Search' />
          </InputGroup>
          <div className='d-flex gap-2'>
            <Button className={styles.button} onClick={toggleClipboard} variant={theme}>
              <div className='align-items-center d-flex gap-1'>
                <Icon icon='paperclip' />
                {clipboardSize}
              </div>
            </Button>
            <BSNavbar.Toggle />
          </div>
        </div>

        <Nav className='me-auto d-none d-lg-flex'>
          {navbarConfig.map(({ href, id, submenu, title }) =>
            submenu ? (
              <NavDropdown className={styles.dropdown} id={id} key={v4()} menuVariant='dark' title={title}>
                {submenu.map(({ alt, className, colors, href, icon_svg_uri, id, submenu, title, value }) =>
                  submenu ? (
                    <NavDropdown
                      className={styles.subDropdown}
                      id={id as string}
                      key={id}
                      menuVariant='dark'
                      title={title}
                    >
                      {submenu.map(({ className, color, colors, href, value }) => (
                        <NavDropdown.Item
                          active={false}
                          as={Link as any}
                          bg='dark'
                          className={className}
                          href={href}
                          key={href}
                          onClick={() => trackNavInternal(href || '')}
                        >
                          <span className={styles.manaRow}>
                            {(color || colors) && <Mana color={color} colors={colors} key={color} />}
                            <span className={styles.text}>{value || title}</span>
                          </span>
                        </NavDropdown.Item>
                      ))}
                    </NavDropdown>
                  ) : (
                    <NavDropdown.Item
                      active={false}
                      as={Link as any}
                      className={className}
                      href={href}
                      key={v4()}
                      onClick={() => trackNavInternal(href || '')}
                    >
                      <span>
                        {icon_svg_uri && (
                          <img alt={alt || (value as string)} className={styles.setIcon} src={icon_svg_uri} />
                        )}
                        {colors && (
                          <span className='me-2'>
                            <Mana colors={colors} />
                          </span>
                        )}
                        {value}
                      </span>
                    </NavDropdown.Item>
                  ),
                )}
              </NavDropdown>
            ) : (
              <Nav.Link
                active={false}
                as={Link as any}
                className={styles.recs}
                href={href}
                key={id}
                onClick={() => trackNavInternal(href || '')}
              >
                {title}
              </Nav.Link>
            ),
          )}
        </Nav>
        <Nav className={styles.buttonContainer}>
          <div className={classNames(styles.buttonContainer, 'd-none d-lg-flex')}>
            <OverlayTrigger overlay={(<Tooltip id='button-tooltip'>Clipboard</Tooltip>) as any} placement='bottom'>
              <Button className={styles.button} onClick={toggleClipboard} variant={theme}>
                <div className='align-items-center d-flex gap-1'>
                  <Icon icon='paperclip' />
                  {clipboardSize}
                </div>
              </Button>
            </OverlayTrigger>
          </div>
          <InputGroup className={classNames(styles.search, 'd-none d-lg-flex')}>
            <AsyncTypeahead className={styles.typeahead} clearOnChange onChange={handleSearch} placeholder='Search' />
          </InputGroup>
          {displayName ? (
            <div className={classNames(styles.buttonContainer, 'd-none d-lg-flex')}>
              <Button
                as={Link as any}
                className={classNames(styles.button, styles.displayName)}
                href='/account'
                onClick={() => trackNavInternal('/account')}
                variant={theme}
              >
                <Icon className={styles.communityIcon} icon='user' />
                <span>{displayName}</span>
              </Button>
            </div>
          ) : (
            <>
              <div className={classNames(styles.buttonContainer, 'd-none d-lg-flex')}>
                <Button
                  as={Link as any}
                  className={styles.button}
                  href={`/signin?redirect=${router.asPath.split('?')[0]}`}
                  onClick={() => trackNavInternal('/signin')}
                  variant={theme}
                >
                  Sign In
                </Button>
                <Button
                  as={Link as any}
                  className={classNames(styles.button, styles.signUp)}
                  href={`/signup?redirect=${router.asPath.split('?')[0]}`}
                  onClick={() => trackNavInternal('/signup')}
                  variant='primary'
                >
                  Sign Up
                </Button>
              </div>
            </>
          )}
          <Button
            className={classNames(styles.hamburgerButton, 'd-flex d-lg-none bg-transparent')}
            onClick={() => setExpanded(!expanded)}
            variant='secondary'
          >
            <AnimatedHamburger open={expanded} />
          </Button>
        </Nav>
      </BSNavbar>

      <BSNavbar bg='dark' className={classNames(styles.navbar2, 'shadow-sm')} variant='dark'>
        <Nav className='me-auto'>
          <SubNavItems />
        </Nav>
        <Nav className={styles.buttonContainer}>
          <div className={styles.buttonContainer}>
            <OverlayTrigger overlay={(<Tooltip id='button-tooltip'>Random</Tooltip>) as any} placement='bottom'>
              <Button aria-label='Random' className={styles.button} onClick={handleRandom} size='sm' variant='dark'>
                <Icon icon='random' />
              </Button>
            </OverlayTrigger>
          </div>
          <div className={styles.buttonContainer}>
            <OverlayTrigger overlay={(<Tooltip id='button-tooltip'>Contact us</Tooltip>) as any} placement='bottom'>
              <Link
                aria-label='Contact us'
                className={classNames([styles.button, 'btn btn-sm btn-dark'])}
                href='/contact-us'
                onClick={() => trackNavInternal('/contact-us')}
              >
                <Icon icon='envelope' />
              </Link>
            </OverlayTrigger>
          </div>
          <div className={styles.buttonContainer}>
            <OverlayTrigger overlay={(<Tooltip id='button-tooltip'>Settings</Tooltip>) as any} placement='bottom'>
              <Button
                aria-label='Settings'
                className={styles.button}
                onClick={() => setShowSettings(true)}
                size='sm'
                variant='dark'
              >
                <Icon icon='cog' />
              </Button>
            </OverlayTrigger>
          </div>
          <span>&nbsp;</span>
        </Nav>
      </BSNavbar>
      <MobileNav
        expanded={expanded}
        handleRandom={handleRandom}
        navBarConfig={navbarConfig}
        setShowSettings={setShowSettings}
        trackNavInternal={trackNavInternal}
      />
    </>
  )
}

export default Navbar
